/**
 * Telegram Mini App "chrome" integration — native header/background colors and
 * safe-area insets. Solves two distinct problems:
 *
 *  1. White header rectangle. Telegram's native top bar (where the ✕ close and
 *     ⋮ menu live) defaults to the client theme's light `bg_color` unless the
 *     app calls `setHeaderColor`. Against the dark cosmos UI that reads as a
 *     jarring white rectangle. We paint the header, the page background and the
 *     bottom bar with `--void` so the native chrome blends into the app and the
 *     two launch entry points (Main Mini App vs Menu button) look identical.
 *
 *  2. App UI overlapping Telegram's own controls. CSS `env(safe-area-inset-*)`
 *     only knows about the device notch/home-bar — it cannot see Telegram's
 *     chrome. Bot API 8.0 exposes `safeAreaInset` and `contentSafeAreaInset`
 *     (the space occupied by the ✕ / ⋮ overlay). We mirror them into CSS
 *     custom properties so layouts can pad away from the chrome. Consumed as
 *     `var(--tg-content-safe-area-inset-top, 0px)` (see MeSubpageShell.css et al).
 *
 * Everything is feature-detected: on older clients (or the dev mock) the
 * methods/insets are absent and we degrade to no-op + 0px.
 */

const VOID = '#05030f'; // --void in tokens.css — universal page background

interface Inset {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

interface TelegramWebAppChrome {
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  onEvent?: (event: string, cb: () => void) => void;
  safeAreaInset?: Inset;
  contentSafeAreaInset?: Inset;
}

function getWebApp(): TelegramWebAppChrome | undefined {
  // Read live from window.Telegram.WebApp (not the @twa-dev/sdk import, which in
  // dev captures the empty shell before the dev-mock swaps it — see telegram.ts).
  return (globalThis as { Telegram?: { WebApp?: TelegramWebAppChrome } }).Telegram?.WebApp;
}

function paintChrome(wa: TelegramWebAppChrome): void {
  // Hex args require Bot API ≥ 6.9; older clients only accept named colors and
  // throw on hex — swallow so a stale client never breaks boot.
  try {
    wa.setBackgroundColor?.(VOID);
  } catch {
    /* unsupported on this client */
  }
  try {
    wa.setHeaderColor?.(VOID);
  } catch {
    /* unsupported on this client */
  }
  try {
    wa.setBottomBarColor?.(VOID); // Bot API ≥ 7.10
  } catch {
    /* unsupported on this client */
  }
}

function setVar(name: string, px: number): void {
  document.documentElement.style.setProperty(name, `${Math.max(0, px)}px`);
}

function writeInset(prefix: string, inset: Inset | undefined): void {
  setVar(`${prefix}-top`, inset?.top ?? 0);
  setVar(`${prefix}-right`, inset?.right ?? 0);
  setVar(`${prefix}-bottom`, inset?.bottom ?? 0);
  setVar(`${prefix}-left`, inset?.left ?? 0);
}

/**
 * Resolve the top inset (px) to reserve below Telegram's content-chrome.
 *
 * We simply mirror Telegram's reported `contentSafeAreaInset.top` (0 when absent).
 * The "content slides under the ✕/⋮ chrome on Main Mini App / Open launches" bug
 * is handled upstream by exiting Telegram fullscreen mode (see
 * lib/telegram-fullscreen.ts) — once out of fullscreen the client draws its own
 * header bar above the webview, so no artificial top padding is needed (and adding
 * one would leave an empty band).
 */
export function resolveContentTopInset(wa: TelegramWebAppChrome): number {
  return wa.contentSafeAreaInset?.top ?? 0;
}

function syncSafeAreas(wa: TelegramWebAppChrome): void {
  // Mirror the OS-level safe area (notch / nav bar) verbatim.
  writeInset('--tg-safe-area-inset', wa.safeAreaInset);

  // Content safe area (Telegram ✕/⋮ chrome): top is launch-mode aware (see
  // resolveContentTopInset); the other edges pass through unchanged.
  setVar('--tg-content-safe-area-inset-top', resolveContentTopInset(wa));
  setVar('--tg-content-safe-area-inset-right', wa.contentSafeAreaInset?.right ?? 0);
  setVar('--tg-content-safe-area-inset-bottom', wa.contentSafeAreaInset?.bottom ?? 0);
  setVar('--tg-content-safe-area-inset-left', wa.contentSafeAreaInset?.left ?? 0);
}

/**
 * Paint the native chrome and start mirroring Telegram's safe-area insets into
 * CSS custom properties. Idempotent-friendly; call once on boot.
 */
export function initTelegramChrome(): void {
  const wa = getWebApp();
  if (!wa) return;

  paintChrome(wa);
  syncSafeAreas(wa);

  const resync = (): void => syncSafeAreas(wa);
  wa.onEvent?.('safeAreaChanged', resync);
  wa.onEvent?.('contentSafeAreaChanged', resync);
  wa.onEvent?.('viewportChanged', resync);
  // Re-paint if the user flips their Telegram theme while the app is open.
  wa.onEvent?.('themeChanged', () => paintChrome(wa));
}
